import { Box, Button, CircularProgress, Paper, Stack } from '@mui/material'
import { CustomModalContext } from 'common/contexts/CustomModalContext'
import { ModalTypes } from 'common/hooks/useCustomModal'
import { useSnackbar } from 'notistack'
import { useContext, useEffect, useState } from 'react'
import i18n from 'src/lang/idiomas'
import InvoicePurchaseSelector from '../components/InvoicePurchaseSelector'
import OrderLines from '../components/OrderLines'
import UploadButton from '../components/UploadButton'
import SendInvoiceHelper from '../helpers/sendInvoiceHelper'
import useFetchOrderLines from '../hooks/useFetchOrderLines'
import useFetchPurchaseOrder from '../hooks/useFetchPurchaseOrder'
import '../style/sendInvoice.css'

const SendInvoice = () => {
  const { response: orderLines, loading, refetch } = useFetchOrderLines()
  const { response: purchaseList, loading: purLoading } = useFetchPurchaseOrder()
  const { handleModal } = useContext(CustomModalContext)
  const [pdfFile, setPdfFile] = useState({ data: '', fileName: '' })
  const [xmlFile, setXmlFile] = useState({ data: '', fileName: '' })
  const [rows, setRows] = useState([])
  const [selectedOrderLines, setSelectOrderLines] = useState([])
  const [purchase, setPurchase] = useState({})
  const { enqueueSnackbar } = useSnackbar()
  const [submitLoader, setSubmitLoader] = useState(false)

  useEffect(() => {
    if (orderLines) {
      setRows(orderLines.map((r) => ({ ...r, enabled: true })))
    }
  }, [orderLines])

  useEffect(() => {
    if (
      pdfFile?.fileName &&
      xmlFile?.fileName &&
      pdfFile.fileName.replace(/\.[^/.]+$/, '') !== xmlFile.fileName.replace(/\.[^/.]+$/, '')
    ) {
      enqueueSnackbar(
        i18n.t('efactura.noconoad', { defaultValue: 'Los nombres de los adjuntos no coinciden' }),
        {
          variant: 'warning',
        },
      )
    }
  }, [pdfFile, xmlFile])

  const resetXmlPdf = () => {
    setPdfFile({ data: '', fileName: '' })
    setXmlFile({ data: '', fileName: '' })
  }

  const resetSubmitedData = () => {
    resetXmlPdf()
    setSelectOrderLines([])
    setPurchase({})
    setSubmitLoader(false)
  }

  const handleSetSelectOrderLines = (ids) => {
    // Marcamos los ids seleccionados
    setSelectOrderLines(ids ?? [])
    // Si no existen ids seleccionados rehabilitamos registros
    if (ids.length === 0) {
      setRows(orderLines.map((r) => ({ ...r, enabled: true })))
      return
    }
    // Recuperamos lineas seleccionadas
    const selectedRows = orderLines.filter((r) => ids.includes(r.id))
    // Filtramos por la 1ª
    const { organizacion_compras, forma_pago, centro_contable } = selectedRows[0]
    // Actualizamos lineas
    const updatedRows = orderLines.map((row) => {
      const sameOrg = row.organizacion_compras === organizacion_compras
      const samePago = row.forma_pago === forma_pago
      const sameCentro = row.centro_contable === centro_contable

      return {
        ...row,
        enabled: sameOrg && samePago && sameCentro,
      }
    })
    // Seteamos lineas actualizadas
    setRows(updatedRows)

    // Filtramos los ids deshabilitados para que no queden seleccionados
    const validIds = ids.filter((id) => {
      const row = updatedRows.find((r) => r.id === id)
      return row?.enabled
    })
    setSelectOrderLines(validIds)
  }

  const submitHandler = () => {
    if (!selectedOrderLines.length) {
      enqueueSnackbar(
        i18n.t('efactura.deselipe', {
          defaultValue: 'Se debe de seleccionar por lo menos una línea de pedido',
        }),
        {
          variant: 'error',
        },
      )
      return
    }
    if (!xmlFile.data && !pdfFile.data && !(purchase.id_detalle && purchase.id_entrada)) {
      enqueueSnackbar(
        i18n.t('efactura.desuunar', {
          defaultValue: 'Se debe de subir al menos un archivo XML o PDF',
        }),
        {
          variant: 'error',
        },
      )
      return
    }

    setSubmitLoader(true)
    SendInvoiceHelper.createInvoice({
      base64_xml_file: xmlFile.data,
      xml_filename: xmlFile.fileName,
      base64_pdf_file: pdfFile.data,
      pdf_filename: pdfFile.fileName,
      id_entrada: String(purchase.id_entrada ?? ''),
      id_detalle: String(purchase.id_detalle ?? ''),
      order_lines_ids: selectedOrderLines,
    })
      .then((res) => {
        enqueueSnackbar(
          res?.data ?? i18n.t('efactura.factcrea', { defaultValue: 'Factura creada' }),
          {
            variant: 'success',
          },
        )
      })
      .catch((err) => {
        const isFiveHundred = err.response.status === 500
        const message = isFiveHundred ? 'Internal Server Error' : (err.response.data ?? err.message)
        enqueueSnackbar(message, { variant: 'error' })
      })
      .finally(() => {
        resetSubmitedData()
        refetch()
      })
  }

  const openPurchaseSelectModal = () => {
    handleModal({
      enabled: true,
      type: ModalTypes.Custom,
      content: (
        <InvoicePurchaseSelector
          setPurchase={(data) => {
            resetXmlPdf()
            setPurchase(data)
            handleModal({ enabled: false })
          }}
          purchaseList={purchaseList}
          clearPurchase={setPurchase}
          selectedPurchase={purchase}
          loading={purLoading}
        />
      ),
    })
  }

  return (
    <Box>
      <Paper sx={{ p: 4, mb: 3 }}>
        <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
          <UploadButton
            fileHandler={setXmlFile}
            title={i18n.t('efactura.subxml', { defaultValue: 'SUBIR XML' })}
            fileType="xml"
            disabled={!!(purchase.id_detalle && purchase.id_entrada)}
            fileData={xmlFile}
          />
          <UploadButton
            fileHandler={setPdfFile}
            title={i18n.t('efactura.subpdf', { defaultValue: 'SUBIR PDF' })}
            fileType="pdf"
            disabled={!!(purchase.id_detalle && purchase.id_entrada)}
            fileData={pdfFile}
          />
        </Stack>
        <Stack
          direction={'row'}
          justifyContent={'space-between'}
          flexWrap={'wrap'}
          gap={3}
          sx={{
            marginTop: 3,
          }}
        >
          <Button
            sx={{ width: '325px', maxWidth: '325px', flexGrow: 1 }}
            variant="outlined"
            onClick={openPurchaseSelectModal}
            disabled={submitLoader}
          >
            {i18n.t('efactura.fasuante', { defaultValue: 'FACTURAS SUBIDAS ANTERIORMENTE' })}{' '}
            {!!purchase.file_name && <span className="purch-s-file">{purchase.file_name}</span>}
          </Button>
          <Button
            sx={{ width: '325px', maxWidth: '325px', flexGrow: 1 }}
            variant="outlined"
            onClick={submitHandler}
            disabled={submitLoader}
          >
            {submitLoader && <CircularProgress size={20} sx={{ position: 'absolute' }} />}
            {i18n.t('general.enviar', { defaultValue: 'ENVIAR' })}
          </Button>
        </Stack>
      </Paper>

      <Box sx={{ mt: 3 }}>
        <OrderLines
          orderLines={rows}
          refetch={refetch}
          loading={loading}
          setOrderLines={handleSetSelectOrderLines}
          selectedOrderLines={selectedOrderLines}
        />
      </Box>
    </Box>
  )
}

export default SendInvoice
